import React from 'react';
import styled from 'styled-components';
import { BuylistItem } from '../../types';
import { getBuylistImageUrl } from '../../services/buylistService';
import { theme, media } from '../../styles/theme';
import {
  Card,
  SubTitle,
  FoilBadge,
  Button as BaseButton,
} from '../../styles/components';

const ItemCard = styled(Card)`
  overflow: hidden;
  transition: ${theme.transitions.normal};
  padding: 0;
  display: flex;
  flex-direction: column;

  ${media.mobile} {
    max-height: calc(100svh - 2rem);
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.cardHover};
  }
`;

const ItemImage = styled.div<{ $imageUrl: string }>`
  aspect-ratio: 3 / 4;
  background-image: ${props => `url(${props.$imageUrl})`};
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-color: ${theme.colors.gray.lightest};

  ${media.mobile} {
    aspect-ratio: auto;
    height: 35svh;
    background-size: contain;
  }
`;

const ItemDetails = styled.div`
  padding: ${theme.spacing.sm};
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const ItemName = styled(SubTitle)`
  margin-bottom: ${theme.spacing.xs};
`;

const ItemInfo = styled.p`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.gray.dark};
  margin-bottom: ${theme.spacing.xs};
`;

const ItemPrice = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: ${theme.spacing.sm};
  font-weight: ${theme.fontWeights.semibold};
`;

const CashPrice = styled.span`
  color: ${theme.colors.primary};
`;

const CreditPrice = styled.span`
  color: ${theme.colors.success};
`;

const AddButton = styled(BaseButton)`
  width: 100%;
  margin-top: ${theme.spacing.sm};

  &:disabled {
    background-color: ${theme.colors.gray.medium};
    cursor: not-allowed;
  }
`;

const QuantityControl = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  margin-right: ${theme.spacing.xs};
`;

const QuantityButton = styled.button`
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${theme.colors.border};
  background-color: ${theme.colors.white};
  color: ${theme.colors.text};
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color ${theme.transitions.fast};
  user-select: none;

  &:first-child {
    border-radius: ${theme.borderRadius.md} 0 0 ${theme.borderRadius.md};
    border-right: none;
  }

  &:last-child {
    border-radius: 0 ${theme.borderRadius.md} ${theme.borderRadius.md} 0;
    border-left: none;
  }

  &:hover:not(:disabled) {
    background-color: ${theme.colors.gray.lightest};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const QuantityValue = styled.span`
  min-width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${theme.colors.border};
  background-color: ${theme.colors.white};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
  color: ${theme.colors.text};
`;

const ItemActions = styled.div`
  margin-top: auto;
`;

const QuantityRow = styled.div`
  display: flex;
  align-items: center;
  margin-top: 0.5rem;
`;

const QuantityLabel = styled.span`
  margin-right: 0.5rem;
`;

interface BuylistItemCardProps {
  item: BuylistItem;
  quantity: number;
  onQuantityChange: (itemId: number, newQuantity: number) => void;
  onAddToBuylist: (item: BuylistItem) => void;
}

const BuylistItemCard: React.FC<BuylistItemCardProps> = ({
  item,
  quantity,
  onQuantityChange,
  onAddToBuylist,
}) => {
  return (
    <ItemCard>
      <ItemImage $imageUrl={getBuylistImageUrl(item.cardBlueprintId, item.image_url)} />
      <ItemDetails>
        <ItemName>{item.blueprintName}</ItemName>
        <ItemInfo>{item.expansionName}</ItemInfo>
        <ItemInfo>{item.blueprintVersion}</ItemInfo>
        {item.foil && <FoilBadge>✨ Foil</FoilBadge>}
        <ItemInfo>Maks antall: {item.maxQuantity}</ItemInfo>

        <ItemActions>
          <ItemPrice>
            <CashPrice>Kontant: {(item.buyPrice || 0).toFixed(2)} kr</CashPrice>
            <CreditPrice>Kreditt: {(item.buylistStoreCreditPrice || 0).toFixed(2)} kr</CreditPrice>
          </ItemPrice>

          <QuantityRow>
            <QuantityLabel>Ant:</QuantityLabel>
            <QuantityControl>
              <QuantityButton
                onClick={() => onQuantityChange(item.buylistID, quantity - 1)}
                disabled={quantity <= 1}
              >
                −
              </QuantityButton>
              <QuantityValue>{quantity}</QuantityValue>
              <QuantityButton
                onClick={() => onQuantityChange(item.buylistID, quantity + 1)}
                disabled={quantity >= item.maxQuantity}
              >
                +
              </QuantityButton>
            </QuantityControl>
          </QuantityRow>

          <AddButton
            onClick={() => onAddToBuylist(item)}
            disabled={item.maxQuantity < 1}
          >
            Legg til i salgskurv
          </AddButton>
        </ItemActions>
      </ItemDetails>
    </ItemCard>
  );
};

export default BuylistItemCard;
